import { LegalShell as Layout } from "@/components/connect/LegalShell";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Mail, Shield, Heart, Clock, CheckCircle, AlertTriangle } from "lucide-react";
import { SEOHead } from "@/components/seo/SEOHead";
import { PAGE_SEO, getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";

/**
 * Financial-difficulty policy for the elefa OTT VoIP wallet service.
 *
 * elefa is prepaid: there is no postpaid bill, no debt, no collections,
 * no late fees and no credit reporting. The traditional TCP-Code hardship
 * framework (designed for postpaid carriers) does not map directly. This page
 * sets out what we will actually do for a user in financial difficulty: pause
 * auto top-ups, extend promotional credit, refund unspent purchased credit
 * where lawful, and refer to free external financial counselling.
 *
 * Australia: we will become a TIO member when our service profile requires it.
 * Until then, AU users can escalate to the OAIC for privacy issues and to AFCA
 * or relevant fair-trading bodies for payment disputes via the original
 * payment method.
 */
const FinancialHardshipPolicy = () => {
  return (
    <Layout>
      <SEOHead
        title={PAGE_SEO.hardship.title}
        description={PAGE_SEO.hardship.description}
        keywords={PAGE_SEO.hardship.keywords}
        canonicalUrl={getCanonicalUrl('/legal/financial-hardship')}
        structuredData={getBreadcrumbSchema([
          { name: 'Home', url: 'https://elefa.tel/' },
          { name: 'Financial Hardship', url: 'https://elefa.tel/legal/financial-hardship' },
        ])}
      />
      <section className="py-12">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <div className="mb-8">
              <Link to="/support">
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Support
                </Button>
              </Link>
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="h-6 w-6 text-primary" />
                <span className="text-sm font-medium text-primary">Financial difficulty support</span>
              </div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Financial Difficulty Policy
              </h1>
              <p className="text-muted-foreground">
                elefa is a prepaid Voice-over-IP service. You only ever
                pay for credit you choose to buy in advance, so there is no bill,
                no debt, no late fee and no collections process to worry about.
                This page explains the help we can offer if you are still finding
                things tight.
              </p>
              <p className="text-xs text-muted-foreground mt-3">
                Version 2.0 · Effective 27 May 2026
              </p>
            </div>

            <Card className="mb-8 border-primary/20 bg-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="h-5 w-5 text-primary" />
                  Our commitment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We will treat you with respect and confidentiality, take
                  reasonable steps to keep you connected to family, and never
                  pressure you into spending more than you can afford on calls.
                </p>
              </CardContent>
            </Card>

            <div className="space-y-8">
              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">1</span>
                  Who this policy is for
                </h2>
                <div className="pl-10">
                  <p className="text-muted-foreground mb-4">
                    This policy applies to any elefa user who is having
                    difficulty paying for the calls they want to make because of
                    circumstances such as:
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2"><span className="text-primary mt-1">•</span>Loss of employment or reduced income</li>
                    <li className="flex items-start gap-2"><span className="text-primary mt-1">•</span>Illness, injury or disability (yours or a family member's)</li>
                    <li className="flex items-start gap-2"><span className="text-primary mt-1">•</span>Family or domestic violence</li>
                    <li className="flex items-start gap-2"><span className="text-primary mt-1">•</span>Natural disaster, displacement or refugee circumstances</li>
                    <li className="flex items-start gap-2"><span className="text-primary mt-1">•</span>Death of a close family member</li>
                    <li className="flex items-start gap-2"><span className="text-primary mt-1">•</span>Any other reasonable cause</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">2</span>
                  What we can do
                </h2>
                <div className="pl-10">
                  <p className="text-muted-foreground mb-4">
                    Because the service is prepaid we cannot offer payment
                    extensions or postpaid plans, but we can do the following:
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                          <Clock className="h-4 w-4 text-primary" />
                          Pause auto top-ups
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          We will switch off any scheduled or low-balance
                          auto-recharge so you are never charged automatically.
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                          <Heart className="h-4 w-4 text-primary" />
                          Extend promotional credit
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Where you hold expiring promotional or referral
                          credit, we can extend the expiry while you work
                          through your situation.
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          Refund unused purchased credit
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Where it is lawful and the original payment method
                          is still valid, we will refund unspent purchased
                          credit on request.
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                          <Shield className="h-4 w-4 text-primary" />
                          Spend controls
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Set a maximum monthly top-up cap on your account so
                          you cannot accidentally spend more than your budget.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">3</span>
                  How to ask for help
                </h2>
                <div className="pl-10">
                  <p className="text-muted-foreground mb-4">
                    Email <strong>hardship@elefa.com.au</strong> from the
                    address on your elefa account and tell us, in your own
                    words, what you would like us to do. We do not require
                    documentation up-front. We aim to respond within 5
                    business days (Melbourne time).
                  </p>
                  <p className="text-muted-foreground">
                    Conversations with our hardship contact are confidential
                    and are handled separately from sales and marketing.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">4</span>
                  What we will not do
                </h2>
                <div className="pl-10">
                  <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• We do not issue postpaid bills, so there is no debt to chase and nothing to refer to a collections agency.</li>
                      <li>• We do not report missed payments to any credit reporting body, because there are no missed payments to report.</li>
                      <li>• We do not charge late fees, account-keeping fees or reconnection fees.</li>
                      <li>• We do not sell your contact details to lenders or to debt-relief services.</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">5</span>
                  Free external support (Australia)
                </h2>
                <div className="pl-10">
                  <div className="space-y-4">
                    <Card>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold text-foreground mb-2">National Debt Helpline</h3>
                        <p className="text-sm text-muted-foreground mb-2">Free, independent financial counselling.</p>
                        <p className="text-sm"><strong>Phone:</strong> 1800 007 007 · <strong>Web:</strong> ndh.org.au</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold text-foreground mb-2">MoneySmart (ASIC)</h3>
                        <p className="text-sm text-muted-foreground mb-2">Government resources for managing money.</p>
                        <p className="text-sm"><strong>Web:</strong> moneysmart.gov.au</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold text-foreground mb-2">Lifeline</h3>
                        <p className="text-sm text-muted-foreground mb-2">24/7 crisis support and suicide prevention.</p>
                        <p className="text-sm"><strong>Phone:</strong> 13 11 14</p>
                      </CardContent>
                    </Card>
                  </div>
                  <p className="text-xs text-muted-foreground mt-4">
                    If you are outside Australia, please look up the equivalent
                    free financial counselling service in your country - most
                    governments and major charities run one.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">6</span>
                  If you are not satisfied
                </h2>
                <div className="pl-10">
                  <div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      Escalation
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      First, write to <strong>complaints@elefa.com.au</strong>.
                      If we cannot resolve it, the next step depends on what your
                      complaint is about:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• <strong>Payments and refunds:</strong> raise a dispute with the card issuer or payment provider used for the purchase. In Australia, the Australian Financial Complaints Authority (AFCA, afca.org.au) handles disputes about payment services.</li>
                      <li>• <strong>Privacy:</strong> the Office of the Australian Information Commissioner (OAIC, oaic.gov.au · 1300 363 992).</li>
                      <li>• <strong>Telecommunications service (Australia):</strong> elefa is not currently a member of the Telecommunications Industry Ombudsman scheme as our service profile does not require membership. We will join when required by law. Until then, please escalate by the above pathways or by contacting ACMA at acma.gov.au.</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="pt-8 border-t">
                <h2 className="text-xl font-bold text-foreground mb-4">Contact us</h2>
                <Card className="border-primary/20 max-w-md">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Mail className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold text-foreground">Email</h3>
                    </div>
                    <p className="text-lg font-bold text-foreground">hardship@elefa.com.au</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Replies within 5 business days, Melbourne time.
                    </p>
                  </CardContent>
                </Card>
              </section>
            </div>

            <div className="mt-12 flex flex-wrap gap-4 text-sm">
              <Link to="/legal/terms" className="text-primary hover:underline">Terms of Service</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/legal/complaints" className="text-primary hover:underline">Complaints Policy</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/regulatory" className="text-primary hover:underline">Regulatory Information</Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FinancialHardshipPolicy;
